<template>
  <div
    class="outreach-contact-header"
    :class="{
      'outreach-contact-header-full': !compact,
      'outreach-contact-header-compact': compact,
    }"
    data-testid="outreach-contact-header"
  >
    <div class="outreach-contact-header__avatar">
      <QSkeleton
        v-if="showSkeleton"
        height="7.0rem"
        type="QAvatar"
        width="7.0rem"
      />
      <OutreachContactAvatar
        v-else
        :key="contact.id"
        :contact="contact"
      />
    </div>

    <div
      v-if="showSkeleton"
      class="tw:flex tw:flex-col tw:gap-2 tw:w-full tw:ml-[-0.1rem] tw:pt-[0.1rem] tw:pr-10"
    >
      <QSkeleton height="30px" />
      <div class="tw:w-full tw:flex tw:flex-row tw:gap-2 tw:pt-1">
        <QSkeleton
          class="tw:w-2/3"
          height="20px"
        />
        <QSkeleton
          class="tw:w-1/3"
          height="20px"
        />
      </div>
      <div class="tw:w-full tw:flex tw:flex-row tw:gap-2">
        <QSkeleton
          class="tw:w-1/2"
          height="20px"
        />
        <div class="tw:flex tw:flex-row tw:gap-2 tw:w-1/2">
          <QSkeleton
            v-for="i in 5"
            :key="i"
            height="1.3rem"
            type="QAvatar"
            width="1.3rem"
          />
        </div>
      </div>

      <div class="outreach-contact-header__profile-action">
        <OutreachContactActions
          v-if="!hideActions"
          :disable="disableActions"
          :selected-ids="[contact.document_id]"
          @activity-created="emit('activityCreated')"
          @refresh="emit('refresh')"
        />
      </div>
    </div>
    <div
      v-else
      class="outreach-contact-header__profile"
      :class="contactClass"
    >
      <RouterLink
        v-if="compact"
        class="outreach-contact-header__profile-name outreach-contact-header__profile-name-link"
        :to="{ name: routeName, params: { id: contact.id } }"
      >
        {{ contact.name }}
      </RouterLink>

      <div
        v-else
        class="outreach-contact-header__profile-name"
      >
        {{ contact.name }}
      </div>

      <div class="outreach-contact-header__profile-details">
        <div
          v-if="contactRole"
          class="outreach-contact-header__profile-detail"
        >
          <span class="outreach-contact-header__profile-detail-icon">
            <QIcon
              name="far fa-address-card"
              size="20px"
            />
          </span>
          <BaseEllipseContent
            class="outreach-contact-header__profile-detail-text"
          >
            {{ contactRole }}
          </BaseEllipseContent>
        </div>

        <div
          v-if="contactOutletType"
          class="outreach-contact-header__profile-detail"
        >
          <span class="outreach-contact-header__profile-detail-icon">
            <span class="outreach-contact-header__profile-detail-indicator" />
          </span>
          {{ contactOutletType }}
        </div>

        <div
          v-if="contactLocation"
          class="outreach-contact-header__profile-detail"
        >
          <span class="outreach-contact-header__profile-detail-icon">
            <QIcon
              name="ion-ios-pin"
              size="20px"
            />
          </span>
          <BaseEllipseContent
            class="outreach-contact-header__profile-detail-text"
          >
            {{ contactLocation }}
          </BaseEllipseContent>
        </div>
      </div>

      <div class="outreach-contact-header__profile-contact">
        <div
          v-if="visibleContactEmails[0]"
          class="outreach-contact-header__profile-detail outreach-contact-header__profile-link"
        >
          <span class="outreach-contact-header__profile-detail-icon">
            <QIcon
              name="email"
              size="20px"
            />
          </span>
          <a :href="`mailto:${visibleContactEmails[0]}`">
            <BaseEllipseContent>
              {{ visibleContactEmails[0] }}
            </BaseEllipseContent>
          </a>
        </div>

        <OutreachContactSocials
          v-if="isSocialContact(contact)"
          :homepages="contact.homepages"
          :socials="contact.socials"
        />
      </div>

      <div class="outreach-contact-header__profile-action">
        <OutreachContactActions
          v-if="!hideActions"
          :disable="disableActions"
          :selected-ids="[contact.document_id]"
          @activity-created="emit('activityCreated')"
          @refresh="emit('refresh')"
        />
      </div>
    </div>

    <slot name="after" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { BaseEllipseContent } from "shared/components/base";
import OutreachContactAvatar from "shared/components/Outreach/OutreachContact/OutreachContactAvatar.vue";
import OutreachContactSocials from "shared/components/Outreach/OutreachContact/OutreachContactSocials.vue";
import OutreachContactActions, {
  type Events,
} from "shared/components/Outreach/OutreachContactActions.vue";
import useContact, {
  type ContactType,
  isSocialContact,
} from "shared/composables/useContact";

const props = defineProps<{
  compact?: boolean;
  contact: ContactType;
  disableActions?: boolean;
  hideActions?: boolean;
  showSkeleton?: boolean;
}>();

const emit = defineEmits<Events>();

const {
  contactRole,
  contactClass,
  visibleContactEmails,
  contactLocation,
  contactOutletType,
  type,
} = useContact(props);

const routeName = computed(() =>
  type.value === "outlet"
    ? "outreach-search-outlet"
    : "outreach-search-influencer"
);
</script>

<style lang="scss" scoped>
@import "shared/css/outreach";

:deep(.c-outreach-contact-avatar) {
  --c-outreach-contact-avatar-width: 110px;
  --c-outreach-contact-avatar-height: 110px;
}

.outreach-contact-header {
  display: flex;
  gap: 24px;

  &__avatar {
    flex-shrink: 0;
  }

  &__profile {
    &-name {
      font-size: var(--s-font-size-500);
      font-weight: var(--s-font-weight-bold);
      line-height: 150%;
      letter-spacing: -0.24px;
      color: var(--s-color-denim-9);

      &-link {
        cursor: pointer;

        &:hover {
          color: var(--s-title-hover);
        }
      }
    }

    &-details {
      display: flex;
      gap: var(--s-spacing-md);
      color: var(--s-color-denim-7);
    }

    &-detail {
      display: flex;
      align-items: center;
      gap: var(--s-spacing-md);

      &-text {
        max-width: 160px;
      }

      &-icon {
        display: inline-flex;
        justify-content: center;
        align-items: center;

        :deep(.q-icon) {
          width: auto;
        }
      }

      &-indicator {
        background-color: var(--c-outreach-contact-color);
        width: 10px;
        height: 10px;
        border-radius: 50%;
      }
    }

    &-link {
      color: var(--s-color-blue-9);

      a {
        max-width: 160px;
        color: var(--s-color-blue-9);
        cursor: pointer;

        &:hover {
          color: var(--s-title-hover);
        }
      }
    }

    &-contact {
      display: flex;
      align-items: center;
      gap: var(--s-spacing-md);
    }

    &-action {
      margin-top: var(--s-spacing-lg);

      @media screen and (width <= 1080px) {
        width: 100%;
      }
    }
  }

  &-compact {
    :deep(.c-outreach-contact-avatar) {
      --c-outreach-contact-avatar-width: 110px;
      --c-outreach-contact-avatar-height: 110px;
    }

    > .outreach-contact-header__profile {
      display: flex;
      flex-direction: column;
      gap: var(--s-spacing-md);
    }
  }

  &-full {
    :deep(.c-outreach-contact-avatar) {
      --c-outreach-contact-avatar-width: 58px;
      --c-outreach-contact-avatar-height: 58px;
    }

    .outreach-contact-header__profile {
      width: 100%;
      display: flex;
      flex-flow: row wrap;
      align-items: center;
      gap: var(--s-spacing-md);

      &-name {
        flex: 100%;
      }

      &-action {
        margin-top: 0;
        margin-left: auto;
      }

      &-detail-text,
      &-link a {
        max-width: 250px;
      }
    }
  }
}
</style>
